import { useCallback, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { NetworkErrorMessage } from '@/components/NetworkErrorMessage';

const CHECK_INTERVAL = 5 * 1000;

export const useNetworkCheck = () => {
  const { t } = useTranslation();
  const rootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);

  const getOrCreateRoot = useCallback(() => {
    if (rootRef.current && containerRef.current?.isConnected) {
      return rootRef.current;
    }

    let container = document.querySelector(
      '[data-network-error]',
    ) as HTMLElement | null;

    if (!container) {
      container = document.createElement('div');
      container.setAttribute('data-network-error', 'true');

      // .l-hero__imgの場合はその下の要素に、.page__contentの場合は子要素として直下に置く
      const heroImg = document.querySelector('.l-hero__img');
      const pageContent = document.querySelector('.page__content');

      if (heroImg) {
        heroImg.insertAdjacentElement('afterend', container);
      } else if (pageContent) {
        pageContent.insertBefore(container, pageContent.firstChild);
      } else {
        return null;
      }
    }

    containerRef.current = container;

    if (!rootRef.current) {
      rootRef.current = createRoot(container);
    }

    return rootRef.current;
  }, []);

  const showError = useCallback(
    (message: string) => {
      const root = getOrCreateRoot();
      if (root) {
        root.render(<NetworkErrorMessage message={message} />);
      }
    },
    [getOrCreateRoot],
  );

  const clearError = useCallback(() => {
    if (rootRef.current) {
      rootRef.current.render(null);
    }
  }, []);

  // オンラインかどうかをチェックし、オフラインであればNetworkErrorを表示
  const checkIsOnline = useCallback((): boolean => {
    const isOnline = navigator.onLine;
    if (isOnline) {
      clearError();
      return true;
    }

    showError(t('errors.api.network_error'));
    return false;
  }, [clearError, showError, t]);

  useEffect(() => {
    const interval = setInterval(() => {
      checkIsOnline();
    }, CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [checkIsOnline]);

  useEffect(() => {
    return () => {
      rootRef.current?.unmount();
      containerRef.current?.remove();
      rootRef.current = null;
      containerRef.current = null;
    };
  }, []);

  return {
    checkIsOnline,
  };
};
